import { useEffect, useState } from "react";
import { X, GripHorizontal } from "lucide-react";

const NOTE_STYLES = {
  amber: "bg-amber-200/95 border-amber-300",
  pink: "bg-pink-200/95 border-pink-300",
  sky: "bg-sky-200/95 border-sky-300",
  emerald: "bg-emerald-200/95 border-emerald-300",
};
const DOTS = {
  amber: "bg-amber-400",
  pink: "bg-pink-400",
  sky: "bg-sky-400",
  emerald: "bg-emerald-400",
};

function Note({ note, zoom, canEdit, onUpdate, onDelete }) {
  const [text, setText] = useState(note.text || "");
  useEffect(() => {
    setText(note.text || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id]);

  const startDrag = (e) => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = note.x;
    const oy = note.y;
    const move = (ev) =>
      onUpdate(note.id, { x: ox + (ev.clientX - sx) / zoom, y: oy + (ev.clientY - sy) / zoom }, false);
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      onUpdate(note.id, { x: ox + (ev.clientX - sx) / zoom, y: oy + (ev.clientY - sy) / zoom }, true);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div
      className={`absolute w-44 rounded-md border shadow-md ${NOTE_STYLES[note.color] || NOTE_STYLES.amber}`}
      style={{ left: note.x, top: note.y, zIndex: 6 }}
      data-testid={`org-note-${note.id}`}
    >
      <div
        className="flex items-center justify-between px-1.5 pt-1"
        onPointerDown={startDrag}
        style={{ cursor: canEdit ? "grab" : "default", touchAction: "none" }}
        data-testid={`org-note-drag-${note.id}`}
      >
        <GripHorizontal className="h-3.5 w-3.5 text-black/30" />
        {canEdit && (
          <div className="flex items-center gap-1">
            {Object.keys(DOTS).map((c) => (
              <button
                key={c}
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => onUpdate(note.id, { color: c }, true)}
                className={`h-3 w-3 rounded-full ${DOTS[c]} ${note.color === c ? "ring-1 ring-black/50" : ""}`}
                data-testid={`org-note-color-${note.id}-${c}`}
              />
            ))}
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onDelete(note.id)}
              data-testid={`org-note-delete-${note.id}`}
            >
              <X className="ml-0.5 h-3.5 w-3.5 text-black/40 hover:text-black/70" />
            </button>
          </div>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => text !== (note.text || "") && onUpdate(note.id, { text }, true)}
        readOnly={!canEdit}
        placeholder="Not yaz..."
        className="h-20 w-full resize-none bg-transparent px-2 pb-2 text-[11px] font-medium leading-snug text-black/80 outline-none placeholder:text-black/30 focus-visible:shadow-none"
        data-testid={`org-note-text-${note.id}`}
      />
    </div>
  );
}

export function StickyNotesLayer({ notes, zoom, canEdit, onUpdate, onDelete }) {
  if (!notes?.length) return null;
  return (
    <>
      {notes.map((n) => (
        <Note key={n.id} note={n} zoom={zoom} canEdit={canEdit} onUpdate={onUpdate} onDelete={onDelete} />
      ))}
    </>
  );
}
